import listing from './assets/mata-symboles.json';

/**
 * A110Z3 — the engravable symbols (David, 7 octobre : « trop d'emoji moches, sois sélectif, enlève ceux trop durs
 * à graver ; on est sur une île, rajoute palmier, tortue ou autre »).
 *
 * 28 solid pictograms in four families — Île (palm, turtle, wave, shell, fish, frangipani, lotus, sunset,
 * volcano, island, sailboat, leaf), Amour, Ciel, Signes — drawn by the « Mata Symboles » font
 * (tools/engrave/symbols_font.py): no hairlines, no small holes, as tall as the capitals. Each sits on the code
 * point of the matching emoji (🌴 🐢 🌊 …) so the workshop recognises it in WhatsApp and the cart, and is inserted
 * followed by U+FE0E (text presentation) so the try-on draws our shape, never a colour emoji. Anything else
 * (colour emoji, the old thin symbols ♡ ☆ ◇ ✝ ❦ …) is stripped from the input.
 */
export type SymbolFamily = 'ile' | 'amour' | 'ciel' | 'signes';

export const SYMBOL_FAMILIES: readonly SymbolFamily[] = ['ile', 'amour', 'ciel', 'signes'];

interface Listed { family: SymbolFamily; char: string; codepoint: string; name: string }
const LISTED = listing as Listed[];

export const SYMBOLS: Record<SymbolFamily, string[]> = {
  ile: LISTED.filter(s => s.family === 'ile').map(s => s.char),
  amour: LISTED.filter(s => s.family === 'amour').map(s => s.char),
  ciel: LISTED.filter(s => s.family === 'ciel').map(s => s.char),
  signes: LISTED.filter(s => s.family === 'signes').map(s => s.char),
};

/** French name of a symbol (palmier, tortue…), for the workshop line. */
export function symbolName(glyph: string): string | null {
  return LISTED.find(s => s.char === glyph)?.name ?? null;
}

/** Text presentation selector appended to every symbol. */
export const TEXT_PRESENTATION = '︎';

/** Every distinct symbol, in family order. */
export const ALL_SYMBOLS: readonly string[] = [...new Set(SYMBOL_FAMILIES.flatMap(f => SYMBOLS[f]))];

export const SYMBOL_COUNT = ALL_SYMBOLS.length;

const escaped = ALL_SYMBOLS.map(s => `\\u{${s.codePointAt(0)!.toString(16)}}`).join('');
/** Anything that is not a letter, a digit, a space, `. , - ' &`, a symbol or the selector. */
export const DISALLOWED = new RegExp(`[^\\p{L}\\p{M}\\p{N}\\p{P}\\p{Sm}\\p{Sc} ${escaped}\\uFE0E]`, 'gu');

export function symbolsOf(family: SymbolFamily): string[] {
  return [...SYMBOLS[family]];
}

/** A symbol ready to insert: glyph + text presentation selector. */
export function symbolToken(glyph: string): string {
  return `${glyph}${TEXT_PRESENTATION}`;
}

/** True for one of our symbols (a whole code point, without its selector). */
export function isSymbol(glyph: string): boolean {
  return ALL_SYMBOLS.includes(glyph);
}
